import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { MaterialModule } from 'src/app/shared/material.module';
import { SnackBarService } from 'src/app/shared/services/snack-service.service';
import { mapTemplateError } from '../template-error';
import { FlatNode, allowedParents } from '../template-tree';
import { WorkClassificationTemplateService } from '../work-classification-template.service';
import { WorkClassificationTemplateNode } from '../work-classification-template.models';

export interface TemplateNodeDialogData {
  templateId: number;
  /** Every node of the template, used for the parent picker. */
  nodes: WorkClassificationTemplateNode[];
  /** Set when editing. */
  node?: WorkClassificationTemplateNode;
  /** Preselected parent when adding. */
  parentId?: number | null;
}

/** Add or edit one template node. Closes with the saved node. */
@Component({
  selector: 'app-template-node-dialog',
  templateUrl: './template-node-dialog.component.html',
  styleUrls: ['./template-node-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [MaterialModule, ReactiveFormsModule, TranslatePipe],
})
export class TemplateNodeDialogComponent implements OnInit {
  readonly data = inject<TemplateNodeDialogData>(MAT_DIALOG_DATA);
  private dialogRef = inject<MatDialogRef<TemplateNodeDialogComponent, WorkClassificationTemplateNode>>(MatDialogRef);
  private fb = inject(FormBuilder);
  private service = inject(WorkClassificationTemplateService);
  private snackBar = inject(SnackBarService);
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  saving = signal(false);
  parents: FlatNode[] = [];

  form = this.fb.group({
    name: this.fb.nonNullable.control('', [Validators.required, Validators.maxLength(200)]),
    description: this.fb.nonNullable.control(''),
    parentId: this.fb.control<number | null>(null),
  });

  ngOnInit(): void {
    // The picker excludes the node itself and its subtree, so a cycle cannot be chosen.
    this.parents = allowedParents(this.data.nodes, this.data.node?.id ?? null);
    const n = this.data.node;
    this.form.patchValue({
      name: n?.name ?? '',
      description: n?.description ?? '',
      parentId: n ? n.parentId : (this.data.parentId ?? null),
    });
  }

  save(): void {
    if (this.form.invalid || this.saving()) {
      this.form.markAllAsTouched();
      return;
    }
    const v = this.form.getRawValue();
    const name = v.name.trim();
    if (!name) {
      this.form.controls.name.setErrors({ required: true });
      return;
    }
    const input = { parentId: v.parentId, name, description: v.description.trim() || null };
    this.saving.set(true);
    const request$: Observable<WorkClassificationTemplateNode> = this.data.node
      ? this.service.updateNode(this.data.templateId, this.data.node.id, { ...input, sortOrder: this.data.node.sortOrder })
      : this.service.addNode(this.data.templateId, input);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: node => {
        this.saving.set(false);
        this.dialogRef.close(node);
      },
      error: err => {
        this.saving.set(false);
        const m = mapTemplateError(err);
        this.snackBar.error(this.translate.instant(m.key, m.params));
      },
    });
  }
}
